// The insect state machine (Phase 7). One file, one table of states, each
// with enter / update / exit. Transitions happen only through go().
//
//   empty -> flyingIn -> landing -> perched -> takingOff -> empty
//
// request(speciesId) is the only input. In `empty` it starts a flight; in
// `perched` it queues the species and sends the current butterfly away
// (that is the dismiss gesture); in every other state it is ignored.
import * as THREE from 'three';
import { config } from '../config.js';
import { Butterfly } from './Butterfly.js';
import { makeArrivalPath, makeExitPath } from './flightPath.js';
import { speciesById } from '../data/species.js';

const UP = new THREE.Vector3(0, 1, 0);
const rand = (min, max) => min + Math.random() * (max - min);

export function createInsectMachine({ scene, camera, perches, log = () => {}, onStateChange = () => {} }) {
  const butterflies = new Map(); // speciesId -> Butterfly
  const loading = new Map(); // speciesId -> Promise
  let state = 'empty';
  let stateSince = 0;
  let now = 0;
  let active = null; // current Butterfly
  let activeSpeciesId = null;
  let queued = null; // speciesId requested while perched
  let perch = null;
  let path = null;
  let pathStart = 0;
  let lastTouch = 0;
  let landFinished = false;
  let perchQuat = new THREE.Quaternion();
  const tmp = { position: new THREE.Vector3(), tangent: new THREE.Vector3() };
  const prevPos = new THREE.Vector3();
  const velocity = new THREE.Vector3();
  const stats = { flapRate: 1, bankDeg: 0, pathU: 0 };

  async function get(speciesId) {
    if (butterflies.has(speciesId)) return butterflies.get(speciesId);
    if (!loading.has(speciesId)) {
      const spec = speciesById[speciesId];
      if (!spec) throw new Error(`unknown species ${speciesId}`);
      const p = Butterfly.load(spec, { log })
        .then((b) => {
          scene.add(b.root);
          butterflies.set(speciesId, b);
          log(`loaded ${speciesId} (${Object.keys(b.actions).join(', ')})`);
          return b;
        })
        .catch((error) => {
          log(`load ${speciesId} failed: ${error.message}`, 'error');
          loading.delete(speciesId);
          throw error;
        });
      loading.set(speciesId, p);
    }
    return loading.get(speciesId);
  }

  function facingForPerch(p) {
    const normal = new THREE.Vector3().fromArray(p.normal).normalize();
    let facing;
    if (config.insect.perchFacing.towardCamera) {
      facing = camera.position.clone().sub(new THREE.Vector3().fromArray(p.position));
    } else {
      facing = new THREE.Vector3(1, 0, 0);
    }
    const jitter = THREE.MathUtils.degToRad(rand(-1, 1) * config.insect.perchFacing.jitterDeg);
    return facing.applyAxisAngle(normal, jitter);
  }

  // Orient the root along the flight tangent with a bank into lateral speed.
  const lookTarget = new THREE.Vector3();
  const flightQuat = new THREE.Quaternion();
  const m4 = new THREE.Matrix4();
  function orientInFlight(b, tangent, dt) {
    if (tangent.lengthSq() < 1e-8) return;
    lookTarget.copy(b.root.position).add(tangent);
    m4.lookAt(lookTarget, b.root.position, UP); // +Z toward target
    flightQuat.setFromRotationMatrix(m4);
    // Bank: roll around the forward axis proportional to lateral velocity.
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(flightQuat);
    const lateral = velocity.dot(right);
    const bank = THREE.MathUtils.clamp(-lateral * config.flight.bankGain, -1, 1) * THREE.MathUtils.degToRad(config.flight.bankMaxDeg);
    stats.bankDeg = THREE.MathUtils.radToDeg(bank);
    flightQuat.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), bank));
    b.root.quaternion.slerp(flightQuat, Math.min(1, dt * 8));
  }

  function flapFromVelocity(b) {
    const fly = b.actions.fly;
    if (!fly) return;
    const rate = THREE.MathUtils.clamp(config.flight.flapBase + config.flight.flapGain * Math.abs(velocity.y), 0.5, config.flight.flapMax);
    stats.flapRate = rate;
    fly.timeScale = rate * config.insect.clipSpeed.fly;
  }

  function moveAlong(b, dt) {
    const t = THREE.MathUtils.clamp((now - pathStart) / path.duration, 0, 1);
    path.sample(t, tmp);
    prevPos.copy(b.root.position);
    b.root.position.copy(tmp.position);
    if (dt > 0) velocity.copy(b.root.position).sub(prevPos).divideScalar(dt);
    stats.pathU = tmp.u;
    return t;
  }

  const states = {
    empty: {
      enter() {
        if (active) active.setVisible(false);
        active = null;
        activeSpeciesId = null;
        perch = null;
        path = null;
        if (queued) {
          const next = queued;
          queued = null;
          request(next);
        }
      },
      update() {},
    },

    flyingIn: {
      enter() {
        perch = perches.pick();
        if (!perch) {
          log('no perches defined; cannot fly in', 'error');
          go('empty');
          return;
        }
        path = makeArrivalPath({ camera, perch });
        pathStart = now;
        // Final orientation on the perch, blended in during landing.
        active.perchAt(perch, facingForPerch(perch));
        perchQuat.copy(active.root.quaternion);
        path.sample(0, tmp);
        active.root.position.copy(tmp.position);
        prevPos.copy(tmp.position);
        active.setVisible(true);
        active.play('fly');
        log(`flyingIn ${activeSpeciesId} -> ${perch.id}, ${path.duration.toFixed(1)} s, ${path.length.toFixed(2)} m`);
      },
      update(dt) {
        const t = moveAlong(active, dt);
        orientInFlight(active, tmp.tangent, dt);
        flapFromVelocity(active);
        // Start the land clip so it ends as the path does (per-species clip length).
        const remaining = (1 - t) * path.duration;
        const lead = config.flight.landClipAtRemainingS ?? active.duration('land');
        if (remaining <= lead) go('landing');
      },
    },

    landing: {
      enter() {
        landFinished = false;
        active.play('land', { loop: false, onFinished: () => (landFinished = true) });
      },
      update(dt) {
        const t = moveAlong(active, dt);
        // Perched only when both the path and the land clip are done; the
        // clip clamps on its last frame while the last centimetres play out.
        if (t >= 1 && landFinished) {
          go('perched');
          return;
        }
        // Blend from flight heading to the perch orientation as we arrive.
        const lead = config.flight.landClipAtRemainingS ?? active.duration('land');
        const k = THREE.MathUtils.smoothstep(t, 1 - lead / path.duration, 1);
        orientInFlight(active, tmp.tangent, dt);
        active.root.quaternion.slerp(perchQuat, k);
        if (t >= 1) {
          active.root.position.copy(path.target);
          active.root.quaternion.copy(perchQuat);
        }
      },
      exit() {
        active.root.position.copy(path.target);
        active.root.quaternion.copy(perchQuat);
      },
    },

    perched: {
      enter() {
        active.play('idle');
        lastTouch = now;
      },
      update() {
        if (now - lastTouch >= config.flight.idleResetS) {
          log('idle reset');
          go('takingOff');
        }
      },
    },

    takingOff: {
      enter() {
        path = makeExitPath({ camera, from: active.root.position.clone() });
        pathStart = now;
        prevPos.copy(active.root.position);
        active.play('takeoff', {
          loop: false,
          onFinished: () => {
            if (state === 'takingOff') active.play('fly');
          },
        });
      },
      update(dt) {
        const t = moveAlong(active, dt);
        orientInFlight(active, tmp.tangent, dt);
        flapFromVelocity(active);
        if (t >= 1) go('empty');
      },
    },
  };

  function go(next) {
    if (!states[next]) throw new Error(`unknown state ${next}`);
    states[state].exit?.();
    const prev = state;
    state = next;
    stateSince = now;
    states[next].enter?.();
    if (state === next) onStateChange(next, prev);
  }

  async function request(speciesId) {
    if (!speciesById[speciesId]) return false;
    lastTouch = now;
    if (state === 'perched') {
      queued = speciesId;
      go('takingOff');
      return true;
    }
    if (state !== 'empty') return false;
    if (queued) return false;
    // Loading is async; hold the request until the model is ready.
    queued = speciesId;
    try {
      const b = await get(speciesId);
      if (state !== 'empty' || queued !== speciesId) return false;
      queued = null;
      active = b;
      activeSpeciesId = speciesId;
      go('flyingIn');
      return true;
    } catch {
      queued = null;
      return false;
    }
  }

  function update(dt, time) {
    now = time;
    states[state].update?.(dt);
    for (const b of butterflies.values()) if (b.root.visible) b.update(dt);
  }

  // Tuning aid: put a species straight onto a perch, skipping the flight.
  async function perchNow(speciesId, p = perches.selected ?? perches.pick()) {
    if (!p) return false;
    const b = await get(speciesId);
    if (active && active !== b) active.setVisible(false);
    active = b;
    activeSpeciesId = speciesId;
    perch = p;
    path = null;
    queued = null;
    b.perchAt(p, facingForPerch(p));
    b.setVisible(true);
    go('perched');
    return true;
  }

  return {
    request,
    perchNow,
    update,
    touch: () => (lastTouch = now),
    preload: (ids) => Promise.allSettled(ids.map(get)),
    force: (next) => {
      if (next === 'empty' || active) go(next);
    },
    get state() {
      return state;
    },
    get speciesId() {
      return activeSpeciesId;
    },
    get perch() {
      return perch;
    },
    get butterfly() {
      return active;
    },
    get timeInState() {
      return now - stateSince;
    },
    stats,
  };
}
